/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import {
   getCommissionSuggestion,
   createCommissionEntry,
} from '../../../../services/routing/routingApi';

// =============================================================================
// CommissionSuggestionModal (handoff 0039 §4) — pré-preenche os lançamentos de
// comissão a partir da SUGESTÃO derivada da seleção planejada do item
// (services[] × catálogo). Leitura pura: a sugestão NÃO persiste nada. O
// financeiro revisa (inclui/exclui linhas, ajusta qtd/valor) e CONFIRMA, o que
// cria os lançamentos (um POST por linha). Continua exigindo item REALIZADO
// (422 item_not_realized) — o botão que abre este modal já gateia por isso.
// =============================================================================

const Body = styled.div`
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const Intro = styled.p`
   margin: 0 0 12px;
   font-size: 0.8rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 0.82rem;

   th,
   td {
      padding: 8px 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      text-align: left;
   }
   th {
      font-size: 0.68rem;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }
   td.num,
   th.num {
      text-align: right;
   }
   td.center,
   th.center {
      text-align: center;
   }

   input[type='number'] {
      width: 72px;
      padding: 5px 7px;
      font-size: 0.82rem;
      border-radius: 6px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      text-align: right;
   }
`;

const InactiveBadge = styled.span`
   display: inline-block;
   margin-left: 6px;
   padding: 1px 6px;
   border-radius: 999px;
   font-size: 0.62rem;
   font-weight: 700;
   background: rgba(180, 83, 9, 0.12);
   color: #b45309;
`;

const TotalRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   padding: 12px 2px 4px;
   font-size: 0.9rem;

   strong {
      font-weight: 700;
   }
`;

const EmptyBox = styled.div`
   padding: 20px;
   text-align: center;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
   font-size: 0.85rem;
   line-height: 1.5;
`;

const ButtonRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 14px;
`;

function formatCurrency(value) {
   const numeric = Number(value);
   if (Number.isNaN(numeric)) return 'R$ 0,00';
   return numeric.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

export default function CommissionSuggestionModal({
   isOpen,
   onClose,
   itemId,
   onConfirmed,
}) {
   const [lines, setLines] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   // Carrega a sugestão ao abrir. Linhas de serviço desativado nascem
   // DESmarcadas (o financeiro opta por incluí-las conscientemente).
   useEffect(() => {
      if (!isOpen || !itemId) return undefined;
      let cancelled = false;
      setIsLoading(true);
      getCommissionSuggestion(itemId)
         .then((suggestion) => {
            if (cancelled) return;
            setLines(
               (suggestion || []).map((s) => ({
                  commissionTableId: s.commission_table_id,
                  serviceKey: s.service_key,
                  serviceDescription: s.service_description,
                  quantity: String(s.quantity ?? 1),
                  unitValue: String(s.unit_value ?? 0),
                  serviceActive: s.service_active !== false,
                  include: s.service_active !== false,
               }))
            );
         })
         .catch((err) => {
            if (cancelled) return;
            toast.error(
               err?.response?.data?.message ??
                  'Não foi possível carregar a sugestão de comissão.'
            );
            setLines([]);
         })
         .finally(() => {
            if (!cancelled) setIsLoading(false);
         });
      return () => {
         cancelled = true;
      };
   }, [isOpen, itemId]);

   const handleClose = useCallback(() => {
      if (isSubmitting) return;
      onClose?.();
   }, [isSubmitting, onClose]);

   const updateLine = (id, patch) => {
      setLines((prev) =>
         prev.map((l) =>
            l.commissionTableId === id ? { ...l, ...patch } : l
         )
      );
   };

   const includedLines = lines.filter((l) => l.include);
   const totalPreview = includedLines.reduce((acc, l) => {
      const qty = Number(l.quantity) || 0;
      const unit = Number(l.unitValue) || 0;
      return acc + qty * unit;
   }, 0);

   const canConfirm =
      !isLoading &&
      includedLines.length > 0 &&
      includedLines.every((l) => {
         const qty = Number(l.quantity);
         const unit = Number(l.unitValue);
         return Number.isFinite(qty) && qty > 0 && Number.isFinite(unit) && unit > 0;
      });

   const handleConfirm = async () => {
      if (!canConfirm || isSubmitting) return;
      setIsSubmitting(true);

      // Cria um lançamento por linha incluída, em sequência. Acumula falhas
      // para reportar quais não entraram (ex.: item saiu de REALIZADO no meio).
      let created = 0;
      const failures = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const line of includedLines) {
         try {
            // eslint-disable-next-line no-await-in-loop
            await createCommissionEntry(itemId, {
               commission_table_id: line.commissionTableId
                  ? Number(line.commissionTableId)
                  : null,
               service_key: line.serviceKey.trim().toUpperCase(),
               service_description: line.serviceDescription.trim(),
               quantity: Number(line.quantity),
               unit_value: Number(line.unitValue),
               notes: null,
            });
            created += 1;
         } catch (err) {
            const apiMessage =
               err?.response?.data?.errors?.[0]?.message ??
               err?.response?.data?.message ??
               'erro';
            failures.push(`${line.serviceKey}: ${apiMessage}`);
         }
      }

      setIsSubmitting(false);

      if (created > 0) {
         toast.success(
            `${created} lançamento(s) de comissão criado(s).`
         );
         onConfirmed?.();
      }
      if (failures.length > 0) {
         toast.error(
            `Não foi possível lançar: ${failures.join(' · ')}`
         );
      }
      if (failures.length === 0) {
         onClose?.();
      }
   };

   if (!isOpen) return null;

   return (
      <Modal
         key={isOpen ? 'open' : 'closed'}
         isOpen={isOpen}
         onClose={handleClose}
         title="Sugestão de comissão"
         width="620px"
      >
         <Body>
            <Intro>
               Sugestão derivada dos serviços planejados do item. Revise as
               linhas, ajuste o que for necessário e confirme — cada linha
               incluída vira um lançamento de comissão.
            </Intro>

            {isLoading && <p style={{ opacity: 0.7 }}>Carregando sugestão…</p>}

            {!isLoading && lines.length === 0 && (
               <EmptyBox>
                  Sem sugestão para este item — nenhum serviço selecionado no
                  planejamento. Use “Novo lançamento” para registrar manualmente.
               </EmptyBox>
            )}

            {!isLoading && lines.length > 0 && (
               <>
                  <Table>
                     <thead>
                        <tr>
                           <th className="center">Incluir</th>
                           <th>Código</th>
                           <th>Descrição</th>
                           <th className="num">Qtd</th>
                           <th className="num">Unitário</th>
                           <th className="num">Total</th>
                        </tr>
                     </thead>
                     <tbody>
                        {lines.map((l) => {
                           const qty = Number(l.quantity) || 0;
                           const unit = Number(l.unitValue) || 0;
                           return (
                              <tr key={l.commissionTableId}>
                                 <td className="center">
                                    <input
                                       type="checkbox"
                                       checked={l.include}
                                       onChange={(e) =>
                                          updateLine(l.commissionTableId, {
                                             include: e.target.checked,
                                          })
                                       }
                                       disabled={isSubmitting}
                                       aria-label={`Incluir ${l.serviceKey}`}
                                    />
                                 </td>
                                 <td>{l.serviceKey}</td>
                                 <td>
                                    {l.serviceDescription}
                                    {!l.serviceActive && (
                                       <InactiveBadge>desativado</InactiveBadge>
                                    )}
                                 </td>
                                 <td className="num">
                                    <input
                                       type="number"
                                       min="1"
                                       step="1"
                                       value={l.quantity}
                                       onChange={(e) =>
                                          updateLine(l.commissionTableId, {
                                             quantity: e.target.value,
                                          })
                                       }
                                       disabled={isSubmitting || !l.include}
                                       aria-label={`Quantidade ${l.serviceKey}`}
                                    />
                                 </td>
                                 <td className="num">
                                    <input
                                       type="number"
                                       min="0.01"
                                       step="0.01"
                                       value={l.unitValue}
                                       onChange={(e) =>
                                          updateLine(l.commissionTableId, {
                                             unitValue: e.target.value,
                                          })
                                       }
                                       disabled={isSubmitting || !l.include}
                                       aria-label={`Valor unitário ${l.serviceKey}`}
                                    />
                                 </td>
                                 <td className="num">
                                    {formatCurrency(qty * unit)}
                                 </td>
                              </tr>
                           );
                        })}
                     </tbody>
                  </Table>

                  <TotalRow>
                     Total a lançar:{' '}
                     <strong>{formatCurrency(totalPreview)}</strong>
                  </TotalRow>
               </>
            )}

            <ButtonRow>
               <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm || isSubmitting}
               >
                  {isSubmitting
                     ? 'Lançando…'
                     : `Confirmar ${includedLines.length || ''} lançamento(s)`}
               </button>
            </ButtonRow>
         </Body>
      </Modal>
   );
}

CommissionSuggestionModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onConfirmed: PropTypes.func,
};
